import { useSphereDialog } from "@components/common/dialog/sphere-dialog-provider";
import { RoleDisplayNames } from "@custom-types/member-types";
import { BaseProjectProps } from "@custom-types/project-types";
import { Typography } from "@mui/material";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { isProjectActiveSelector } from "@store/projects/projects-selector";
import {
  addProcessingProjects,
  removeProcessingProjects,
} from "@store/projects/projects-slice";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback } from "react";

/** Type of the callback returned by the custom hook */
type ProjectArchiveDialogFn = () => Promise<boolean>;

/**
 * Custom hook that returns a function to archive or unarchive a project.
 */
export function useProjectArchiveDialog({
  project,
}: BaseProjectProps): ProjectArchiveDialogFn {
  const { createDialog } = useSphereDialog();
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();

  const isProjectActive = useAppSelector(isProjectActiveSelector(project.id));

  const handleDialog = useCallback(async () => {
    // https://help.holobuilder.com/en/articles/3435523-how-to-archive-a-project-in-the-dashboard
    const whoCanViewArchived = [
      `${RoleDisplayNames.COMPANY_EXECUTIVE}s,`,
      `${RoleDisplayNames.COMPANY_MANAGER}s and`,
      `${RoleDisplayNames.PROJECT_MANAGER}s of this project`,
    ].join(" ");

    // Creates a modal dialog and waits for the user to perform an action
    const hasConfirmed = await createDialog(
      {
        title: isProjectActive ? "Archive Project" : "Unarchive Project",
        confirmText: isProjectActive ? "Archive" : "Unarchive",
        isHideClose: true,
      },
      <>
        <Typography>
          Do you really want to {isProjectActive ? "archive" : "unarchive"} this
          project?
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            mt: "16px",
          }}
        >
          {isProjectActive
            ? `In archived state, only ${whoCanViewArchived} will be able to view the project online.`
            : "Afterwards, all project members will be able to access the project again."}
        </Typography>
      </>
    );

    if (hasConfirmed) {
      trackEvent({
        name: isProjectActive
          ? ProjectEvents.archiveProject
          : ProjectEvents.unarchiveProject,
      });

      // Add the projectId to processing projects to start the loading spinner in the project
      dispatch(addProcessingProjects([project.id]));

      const payload: FaroCoreApiTypes.UpdateProjectDetailsPayloadProps = {
        archivingState: isProjectActive ? "ARCHIVED" : "UNARCHIVED",
      };

      void dispatch(updateProjectDetails({ payload }));

      // TODO: Maybe move adding/removing processing projects to the pending/fulfilled in project-slice
      // Remove projectId from processing project to stop the loading spinner in the project
      dispatch(removeProcessingProjects([project.id]));
      return true;
    }

    return false;
  }, [createDialog, isProjectActive, dispatch, project, trackEvent]);

  return handleDialog;
}
